import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CircleIcon from '@mui/icons-material/Circle';
import { Link } from 'react-router-dom';
import { formatDate } from '../../utils/functions';
import { useLang, uiText, translateStatus, orderCardSubtext } from '../../utils/i18n';

const statusColors = {
    Processing: { color: "text-primary-green", outline: true },
    Accepted: { color: "text-blue-600", outline: false },
    Packed: { color: "text-blue-600", outline: false },
    Shipped: { color: "text-primary-orange", outline: false },
    Delivered: { color: "text-primary-green", outline: false },
    Cancelled: { color: "text-red-500", outline: false },
};

const OrderCard = ({ orderId, orderNumber, orderItems, totalPrice, orderStatus, createdAt, deliveredAt, assignedDriver, pointsEarned, walletUsed }) => {

    const { lang } = useLang();
    const itemCount = orderItems.reduce((sum, item) => sum + item.quantity, 0);
    const status = statusColors[orderStatus] || statusColors.Processing;

    const statusLabel = () => {
        if (orderStatus === 'Processing') return `${uiText('orderedOn', lang)} ${formatDate(createdAt)}${uiText('orderedOnSuffix', lang)}`;
        if (orderStatus === 'Delivered') return `${uiText('deliveredOn', lang)} ${formatDate(deliveredAt)}${uiText('deliveredOnSuffix', lang)}`;
        return translateStatus(orderStatus === 'Accepted' ? 'Order Accepted' : orderStatus === 'Packed' ? 'Order Packed' : orderStatus, lang);
    };

    return (
        <Link to={`/order_details/${orderId}`} className="flex flex-col sm:flex-row p-4 items-start bg-white border rounded gap-3 hover:shadow-lg">

            <div className="flex gap-1 flex-shrink-0">
                {orderItems.slice(0, 3).map((item, i) => (
                    <div key={i} className="w-16 h-16 border rounded overflow-hidden bg-gray-50">
                        <img draggable="false" className="h-full w-full object-contain" src={item.image} alt={item.name} />
                    </div>
                ))}
                {orderItems.length > 3 && (
                    <div className="w-16 h-16 border rounded bg-gray-50 flex items-center justify-center text-xs text-gray-500 font-medium">
                        +{orderItems.length - 3}
                    </div>
                )}
            </div>

            <div className="flex flex-col sm:flex-row justify-between w-full">
                <div className="flex flex-col gap-1">
                    <p className="text-xs text-gray-400 font-medium">{uiText('orderNumberHash', lang)}{orderNumber ?? orderId}</p>
                    <p className="text-sm font-medium">
                        {orderItems[0]?.name}{orderItems.length > 1 ? ` ${uiText('andMoreItem', lang)} ${orderItems.length - 1} ${uiText('moreItemsWord', lang)}${orderItems.length > 2 && lang === 'en' ? 's' : ''}` : ''}
                    </p>
                    <p className="text-xs text-gray-500">{itemCount} {uiText('itemsCountWord', lang)}{itemCount !== 1 && lang === 'en' ? 's' : ''} · {uiText('totalLabel', lang)} HK${totalPrice.toLocaleString()}</p>
                    <div className="flex gap-2">
                        {pointsEarned > 0 && (
                            <span className="text-xs text-primary-orange font-medium">🎁 {pointsEarned.toLocaleString()} {uiText('ptsEarned', lang)}</span>
                        )}
                        {walletUsed && (
                            <span className="text-xs text-primary-green font-medium">💳 {uiText('walletUsedLabel', lang)}</span>
                        )}
                    </div>
                    {assignedDriver && (
                        <p className="text-xs text-gray-500">{uiText('driverLabel', lang)} <span className="font-medium text-gray-700">{assignedDriver.name}</span></p>
                    )}
                </div>

                <div className="flex flex-col gap-1.5 mt-2 sm:mt-0">
                    <p className={`text-sm font-medium flex items-center gap-1 ${status.color}`}>
                        {status.outline ? <RadioButtonUncheckedIcon sx={{ fontSize: "14px" }} /> : <CircleIcon sx={{ fontSize: "14px" }} />}
                        <span className="text-gray-800">{statusLabel()}</span>
                    </p>
                    <p className="text-xs ml-1 text-gray-500">{orderCardSubtext(orderStatus, lang)}</p>
                </div>
            </div>
        </Link>
    );
};

export default OrderCard;
